// src/chat/chatApi.ts
import { apiClient } from "@maat/api-base";
var chatApi = {
  getConversations: async (params) => {
    return apiClient.get("/chat/api/conversations", { params });
  },
  /**
   * ONE thread, by id — the deep-link resolver: a URL can name a conversation no
   * page the client holds contains. Scoped by the SAME profile legs the list
   * uses, so it widens nothing: a thread the caller's own leg would not list
   * comes back 404, indistinguishable from one that does not exist.
   */
  getConversation: async (conversationId) => {
    return apiClient.get(`/chat/api/conversations/${conversationId}`);
  },
  /**
   * Archive / unarchive a thread in the CALLER's own inbox. Guest-only and
   * per-participant — the server 403s any other profile and the flag never
   * reaches the counterpart's list. Idempotent and body-less: a second archive
   * returns the first one's stamp rather than re-stamping it.
   */
  archiveConversation: async (conversationId) => {
    return apiClient.post(`/chat/api/conversations/${conversationId}/archive`);
  },
  unarchiveConversation: async (conversationId) => {
    return apiClient.post(`/chat/api/conversations/${conversationId}/unarchive`);
  },
  createInboxConversation: async (data) => {
    return apiClient.post("/chat/api/conversations/inbox", data);
  },
  lookupInboxConversation: async (params) => {
    return apiClient.get("/chat/api/conversations/inbox/lookup", { params });
  },
  /** The booking's thread on the caller's own leg, else `null` — never a 404. */
  lookupConversationByBooking: async (bookingId) => {
    return apiClient.get(`/chat/api/conversations/by-booking/${bookingId}`);
  },
  createDirectConversation: async (data) => {
    return apiClient.post("/chat/api/conversations/direct", data);
  },
  getMessages: async (conversationId, params) => {
    return apiClient.get(`/chat/api/conversations/${conversationId}/messages`, { params });
  },
  sendMessage: async (conversationId, data) => {
    return apiClient.post(`/chat/api/conversations/${conversationId}/messages`, data);
  },
  sendMessageAttachments: async (conversationId, { files, text, durations }) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    if (text?.trim()) formData.append("text", text.trim());
    if (durations?.some((d) => Number.isFinite(d))) {
      formData.append(
        "meta",
        JSON.stringify(files.map((_, i) => ({ duration_seconds: durations[i] })))
      );
    }
    return apiClient.post(
      `/chat/api/conversations/${conversationId}/messages/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },
  markRead: async (conversationId, data) => {
    return apiClient.post(`/chat/api/conversations/${conversationId}/read`, data);
  },
  sendMessageRequest: async (data) => {
    return apiClient.post("/chat/api/message-requests", data);
  },
  getMessageRequests: async () => {
    return apiClient.get("/chat/api/message-requests");
  },
  respondToMessageRequest: async (id, data) => {
    return apiClient.patch(`/chat/api/message-requests/${id}`, data);
  },
  getInternalNotes: async (conversationId, params) => {
    return apiClient.get(`/chat/api/conversations/${conversationId}/notes`, { params });
  },
  createInternalNote: async (conversationId, data) => {
    return apiClient.post(`/chat/api/conversations/${conversationId}/notes`, data);
  },
  summarize: async (conversationId) => {
    return apiClient.get(`/chat/api/conversations/${conversationId}/summarize`);
  },
  getSSEUrl: () => {
    return "/chat/api/sse";
  },
  getInboxAssignees: async (inboxId) => {
    return apiClient.get(`/chat/api/inbox/${inboxId}/assignees`);
  },
  assignInboxContact: async (inboxId, data) => {
    return apiClient.post(`/chat/api/inbox/${inboxId}/assign`, data);
  },
  unassignInboxContact: async (inboxId, data) => {
    return apiClient.post(`/chat/api/inbox/${inboxId}/unassign`, data);
  }
};

export {
  chatApi
};
